import React, { useCallback, forwardRef, type ForwardedRef } from 'react';
import { xcss, Box, Anchor } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { getColorMetadata } from '../color-picker/utils/index.tsx';
import { memoizeGetAutoGeneratedColorForLabels } from '../option-color-generator/index.tsx';
import type { Props } from './types.tsx';

export const ColoredTag = forwardRef<HTMLElement, Props>(
	(
		{
			labelName,
			href,
			actionSubjectId,
			containerXcss,
			contentXcss,
			analyticsAttributes,
			color,
			...restProps
		},
		ref,
	) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const { backgroundColor: borderColor } = getColorMetadata(
			memoizeGetAutoGeneratedColorForLabels(labelName),
		);

		const onHandleClickForAnchor = useCallback(() => {
			if (actionSubjectId) {
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'clicked',
						actionSubject: 'link',
						attributes: analyticsAttributes,
					}),
					actionSubjectId,
				);
			}
		}, [actionSubjectId, createAnalyticsEvent, analyticsAttributes]);

		if (href) {
			return (
				<Anchor
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					ref={ref as ForwardedRef<HTMLAnchorElement>}
					xcss={[
						tagStyles,
						hoverStyles,
						anchorTextStyles,
						...(containerXcss ? [containerXcss] : []),
					]}
					href={href}
					// eslint-disable-next-line jira/react/no-style-attribute
					style={{ borderColor: color || borderColor }}
					onClick={onHandleClickForAnchor}
					{...restProps}
				>
					<Box xcss={[tagTextStyles, ...(contentXcss ? [contentXcss] : [])]}>{labelName}</Box>
				</Anchor>
			);
		}
		return (
			<Box
				ref={ref}
				xcss={[tagStyles, ...(containerXcss ? [containerXcss] : [])]}
				// eslint-disable-next-line jira/react/no-style-attribute
				style={{ borderColor: color || borderColor }}
			>
				<Box xcss={[tagTextStyles, ...(contentXcss ? [contentXcss] : [])]}>{labelName}</Box>
			</Box>
		);
	},
);

const tagStyles = xcss({
	display: 'flex',
	backgroundColor: 'color.background.neutral.subtle',
	borderStyle: 'solid',
	borderWidth: 'border.width',
	borderRadius: token('radius.small'),
	paddingInline: 'space.050',
	paddingBlock: '0',
	font: 'font.body',
	marginBlock: 'space.050',
	marginInline: 'space.050',
	height: '20px',
	alignItems: 'center',
	maxWidth: '100%',
});

const hoverStyles = xcss({
	':hover': {
		backgroundColor: 'color.background.neutral.hovered',
		textDecoration: 'underline',
	},
});

const tagTextStyles = xcss({
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	maxWidth: '180px',
});

const anchorTextStyles = xcss({
	color: 'color.text',
	textDecoration: 'none',
	':hover': {
		color: 'color.text',
	},
});
